/**
 * Article badge detection + presentation.
 *
 * Detects which visual badge should be applied to an article based on
 * keywords in its title or tags. Priority order (highest first):
 *   1. EXCLUSIVE  → gold badge + legal notice appended to body
 *   2. BREAKING   → red urgent badge
 *   3. CATCH UP   → purple badge with a newspaper emoji
 *   4. OPINION    → blue/violet editorial badge
 *
 * A single article can only carry one badge - the highest-priority match wins.
 */

const KIND_KEYWORDS = {
  exclusive: /\bexclusive\b/i,
  breaking: /\bbreaking\b/i,
  // Auto-applies to "UPDATE | ..." and "CATCH UP | ..." kicker headlines, or
  // any headline that says catch up.
  catchup: /(^\s*(update|catch[-\s]?up)\s*[|:–-])|\bcatch[-\s]?up\b/i,
  opinion: /\bopinion\b/i,
};

const KIND_TAG = {
  exclusive: 'exclusive',
  breaking: 'breaking',
  catchup: 'catch up',
  opinion: 'opinion',
};

// Extra tag spellings that should still match.
const KIND_TAG_ALIASES = {
  catchup: ['catchup', 'catch-up', 'catch up'],
};

const PRIORITY = ['exclusive', 'breaking', 'catchup', 'opinion'];

/** Returns 'exclusive' | 'breaking' | 'catchup' | 'opinion' | null. */
export const getArticleBadgeKind = (article) => {
  if (!article) return null;
  const title = article.title || '';
  const tags = (article.tags || []).map((t) => String(t).toLowerCase().trim());
  for (const kind of PRIORITY) {
    const aliases = KIND_TAG_ALIASES[kind] || [KIND_TAG[kind]];
    if (KIND_KEYWORDS[kind].test(title) || aliases.some((a) => tags.includes(a))) {
      return kind;
    }
  }
  return null;
};

/** Kept for backwards compat with existing call sites. */
export const isExclusiveArticle = (article) => getArticleBadgeKind(article) === 'exclusive';

/**
 * Style definitions per badge kind. Consumers pick their own layout (pill,
 * ribbon, corner-tag) and use these tokens for the visual language.
 */
export const BADGE_STYLES = {
  exclusive: {
    label: 'Outlaw Exclusive',
    labelShort: 'Exclusive',
    // Flat solid gold - VICE-style kicker, no gradient/glow
    className: 'bg-yellow-400 text-black',
    classNameLarge: 'bg-yellow-400 text-black',
    icon: 'Crown',
    pulse: false,
  },
  breaking: {
    label: 'Breaking',
    labelShort: 'Breaking',
    // Flat red - no glow, no pulse
    className: 'bg-red-600 text-white',
    classNameLarge: 'bg-red-600 text-white',
    icon: 'Zap',
    pulse: false,
  },
  catchup: {
    label: 'Catch Up',
    labelShort: 'Catch Up',
    // Flat purple
    className: 'bg-purple-600 text-white',
    classNameLarge: 'bg-purple-600 text-white',
    icon: 'Newspaper',
    pulse: false,
  },
  opinion: {
    label: 'Opinion',
    labelShort: 'Opinion',
    // Flat violet
    className: 'bg-violet-500 text-white',
    classNameLarge: 'bg-violet-500 text-white',
    icon: 'MessageSquareQuote',
    pulse: false,
  },
};

export const getBadgeStyle = (article) => {
  const kind = getArticleBadgeKind(article);
  return kind ? { kind, ...BADGE_STYLES[kind] } : null;
};

// ---------------------------------------------------------------------------
// Legal notice - appended only to EXCLUSIVE articles.
// ---------------------------------------------------------------------------

export const EXCLUSIVE_LEGAL_NOTICE_HTML = `
<div class="not-prose exclusive-legal-notice" role="note" data-testid="article-legal-notice">
  <div class="legal-header">
    <span class="legal-badge">LEGAL NOTICE · OUTLAW MEDIA EXCLUSIVE</span>
  </div>
  <p class="legal-body">
    This is an <strong>Outlaw Media exclusive</strong>. If any mainstream outlet,
    publication, broadcaster or online commentator uses information, quotes,
    imagery, footage or investigative material sourced from this reporting
    <strong>without providing clear attribution to Outlaw Media (outlawmedia.com.au)
    and its author, Ryan Naumenko</strong>, legal action will commence
    <strong>without warning or notification</strong>.
  </p>
  <p class="legal-body">
    Use of this material - in whole or in part - constitutes acceptance of these
    terms. All rights reserved.
  </p>
</div>`;

export const EXCLUSIVE_NOTICE_CSS = `
.exclusive-legal-notice {
  margin-top: 3rem;
  padding: 1.5rem 1.5rem 1.5rem 1.75rem;
  background: linear-gradient(135deg, rgba(234,179,8,0.08) 0%, rgba(0,0,0,0.9) 55%, rgba(220,38,38,0.08) 100%);
  border: 1px solid rgba(234,179,8,0.35);
  border-left: 4px solid #eab308;
  border-radius: 0.75rem;
  color: #e5e5e5;
  box-shadow: 0 0 32px -12px rgba(234,179,8,0.25);
}
.exclusive-legal-notice .legal-header { margin-bottom: 0.75rem; }
.exclusive-legal-notice .legal-badge {
  display: inline-block;
  font-size: 0.65rem;
  letter-spacing: 0.3em;
  font-weight: 900;
  color: #eab308;
  text-transform: uppercase;
}
.exclusive-legal-notice .legal-body {
  color: #d4d4d8;
  font-size: 0.925rem;
  line-height: 1.6;
  margin: 0 0 0.85rem 0;
}
.exclusive-legal-notice .legal-body:last-child { margin-bottom: 0; }
.exclusive-legal-notice .legal-body strong { color: #ffffff; }
`;
